import { FormArray, Validators } from "@angular/forms";
import { SearchResourceType } from "@app/core/model/search/search-resource-type";
import { BasespaceFormControl, BasespaceFormProperties } from "../bs-app-launch-form-control";
import { BsAppLaunchResourceChooserControlComponent } from "./bs-app-launch-resource-chooser-control.component";
import { Subject } from "rxjs";
import { RowAction } from "../bs-app-launch-tabular-field-set-control/tabular-field-set.control";
import { StringUtilities } from "@app/core/utilities/string-utilities";
import { cloneDeep } from "lodash";

// Doesn't contain all resource data but has enough to reference/populate the resource
export interface ResourceReference {
    id: number | string;
    name: string;
    type?: string;
    attributes?: any;
}

export interface ResourceChooserFormControlProperties extends BasespaceFormProperties<BsAppLaunchResourceChooserControlComponent> {
    resourceType: SearchResourceType;
    allowedPermissions: any[];
    multiselect: boolean;
    valueType: 'Input' | 'Output';
    allowResourceCreation: boolean;

    // Only for file chooser
    extensionFilters?: string;
    
    // Only for dataset file chooser. I suspect this was added for backwards compatibility reasons
    datasetFiles?: ResourceReference[];

    // Only for datasets
    datasetResourceInfos?: { id: number; name: string; datasetIdToken: string }[];

    // Only for biosamples
    // There are a bunch of other properties on this field but we don't need them so I left them out in type definition
    datasetsByBioSample?: { 
        libraryPrepKitName: string; 
        libraryPrepKitId: string;
        bioSample: { name: string; id: string };
        attributes: any;
    }[];
    bioSamples?: ResourceReference[];

    // Only for samples
    genomeFilters?: string;

    // This is for initial resources supplied to input from the GET /launchinfo endpoint (for requeuing)
    resources: ResourceReference[];

    searchQuery: string;

    // Can only be following fields: radio buttons, checkboxes, dropdown/select
    displayFields?: BasespaceFormProperties<any>[];
    fieldCells?: BasespaceFormProperties<any>[][];

    displayFieldsTabularFieldSetControl?: FormArray;
    rowActionSubject?: Subject<RowAction>;
    overrideDefaultSize?: string;
}

// Reference docs: https://dagobah-developer.cloud-test.illumina.com/docs/content/documentation/apptools/formbuilder-overview#ProjectChooser
// Reference class: src/Cloud.FormBuilder/Models/RunChooser.cs


export class ResourceChooserFormControl extends BasespaceFormControl<BsAppLaunchResourceChooserControlComponent> implements ResourceChooserFormControlProperties {
    resourceType: SearchResourceType;
    allowedPermissions: any[];
    multiselect: boolean;
    valueType: "Input" | "Output";
    resources: ResourceReference[];
    searchQuery: string;
    allowResourceCreation: boolean;

    displayFields?: BasespaceFormProperties<any>[];
    displayFieldsTabularFieldSetControl?: FormArray;
    fieldCells?: BasespaceFormProperties<any>[][];
    rowActionSubject: Subject<RowAction>;
    overrideDefaultSize?: string;

    // comma separated values ".bam,.vcf"
    extensionFilters?: string;
    datasetFiles?: ResourceReference[];
    genomeFilters?: string;

    constructor(
        data: ResourceChooserFormControlProperties
    ) {
        const { 
            id, 
            componentReference, 
            label, 
            helpText, 
            size,
            value, 
            required, 
            requiredMessage, 
            resourceType, 
            allowedPermissions, 
            multiselect, 
            valueType, 
            resources,
            datasetFiles,
            datasetResourceInfos,
            datasetsByBioSample,
            bioSamples,
            searchQuery,
            allowResourceCreation,
            extensionFilters,
            tabularFieldSetStyling,
            genomeFilters,
            displayFields,
            formControlType
        } = data;
        
        super({ 
            id, 
            componentReference, 
            helpText, 
            size,
            value, 
            required, 
            requiredMessage,
            tabularFieldSetStyling,
            formControlType
        });

        this.resourceType = resourceType;
        this.allowedPermissions = allowedPermissions;
        this.multiselect = multiselect;
        this.valueType = valueType;
        this.displayFields = (displayFields && displayFields.length > 0) ? displayFields : null;

        if(this.displayFields) {
            this.displayFields.forEach((field) => {
                field.tabularFieldSetStyling = true;
            });
        }

        this.overrideDefaultSize = this.displayFields ? '100%' : null;
        

        this.displayFieldsTabularFieldSetControl = this.displayFields ? new FormArray([]) : null;
        this.rowActionSubject = new Subject();

        switch(this.resourceType) {
            case SearchResourceType.File:
                this.resources = datasetFiles;
                break;
            case SearchResourceType.Dataset:
                const reformattedResources = datasetResourceInfos.map((resource) => {
                    return {
                        ...resource,
                        id: resource.datasetIdToken
                    } as ResourceReference
                });
                this.resources = reformattedResources;
                break;
            case SearchResourceType.Biosample:
                // Create flat composite resources for each biosample+libraryprep combination
                const flatBiosamples: ResourceReference[] = [];
                const seenCompositeIds = new Set<string>();
                
                datasetsByBioSample.forEach((datasetByBiosample) => {
                    const { libraryPrepKitName, libraryPrepKitId, bioSample, attributes } = datasetByBiosample;
                    const baseBiosample = bioSamples.find((bs) => bs.id === bioSample.id);
                    const compositeId = `${bioSample.id}::${libraryPrepKitId}`;
                    
                    // Only add if this composite ID doesn't already exist
                    if (!seenCompositeIds.has(compositeId)) {
                        seenCompositeIds.add(compositeId);
                        flatBiosamples.push({
                            id: compositeId,
                            name: `${baseBiosample.name}: ${libraryPrepKitName}`,
                            attributes: attributes
                        });
                    }
                });
                this.resources = flatBiosamples;
                break;
            case SearchResourceType.Sample:
                this.genomeFilters = genomeFilters;

                // We don't want to populate sample chooser with extrapolated sample data from biosamples
                // ie user submitted form with biosamples but they requeued the analysis with "input data" set to "samples..."
                if(!bioSamples || bioSamples.length === 0) {
                    this.resources = resources;
                }
                break;
            default:
                this.resources = resources;
        }
        
        this.label = this.getReplacementText(label || '');
        this.searchQuery = searchQuery;

        this.allowResourceCreation = allowResourceCreation || false;
        this.extensionFilters = extensionFilters ? extensionFilters : null;

        if(required) {
            this.setValidators(Validators.required);
        }

        if(this.resources) {
            const value = this.resources.map((resource) => resource.id);
            this.setValue(value);
        }

        if(this.displayFields && this.resources && this.resources.length > 0) {
            const fieldCells = [];
            this.resources.forEach((resource) => {
                const fieldRow = [];

                this.displayFields.forEach((field) => {
                    const clonedField = cloneDeep(field);
                    const fieldKey = StringUtilities.findKeyIgnoreCase(resource.attributes, `input.${StringUtilities.toCamelCase(field.id)}`);
                    const fieldValueArray = fieldKey ? resource.attributes[fieldKey] : null;
                    const fieldValue = fieldValueArray ? fieldValueArray[0] : null;
                    if(fieldValue) {
                        fieldRow.push(this.setFieldValue(clonedField, fieldValue));
                    } else {
                        fieldRow.push(clonedField);
                    }
                });

                fieldCells.push(fieldRow);
            });

            this.fieldCells = fieldCells;
        }
    }

    /**
     * Sets the value on a field properties object based on its formControlType.
     * For Select/RadioButton controls, sets the appropriate choice's `selected` property.
     * For CheckBox controls, sets the appropriate choice's `checked` property.
     * For other controls, sets the `value` property directly.
     * 
     * @param field The field properties object to update
     * @param value The value to set
     * @returns The modified field properties object
     */
    setFieldValue(field: BasespaceFormProperties<any>, value: any): BasespaceFormProperties<any> {
        switch(field.formControlType) {
            case 'Select':
                if (field['choices']) {
                    field['choices'].forEach((choice: { selected: boolean; value: any }) => {
                        choice.selected = choice.value === value;
                    });
                }
                break;
            case 'RadioButton':
                field['value'] = value;
                break;
            case 'CheckBox':
                // For CheckBox, set checked = true for matching values (can be array for multiselect)
                if (field['choices']) {
                    const valuesToCheck = Array.isArray(value) ? value : [value];
                    field['choices'].forEach((choice: { checked: boolean; value: any }) => {
                        choice.checked = valuesToCheck.includes(choice.value);
                    });
                }
                break;
            default:
                // For other control types, set value directly
                field.value = value;
                break;
        }

        return field;
    }

    transformResourceIdToFormDataId(id: string): string {
        switch(this.resourceType) {
            case SearchResourceType.File:
                return `dsf.${id}`;
            case SearchResourceType.Dataset:
                return `dsit.${id}`
            default:
                return id;
        }
    }


    getReplacementText(input: string): string {
        switch(this.resourceType) {
            case SearchResourceType.Dataset:
                return input.replace("an app result", "a dataset").replace("an App Result", "a Dataset")
                    .replace("app result", "dataset").replace("App Result", "Dataset");  
            case SearchResourceType.AppResult:
                return input.replace("a dataset", "an app result").replace("a Dataset", "an App Result")
                    .replace("dataset", "app result").replace("Dataset", "App Result"); 
            case SearchResourceType.Biosample:
                // Use negative lookbehind to only replace "sample" when not preceded by "bio"
                return input.replace(/(?<!bio)sample/gi, (match) => {
                    return match === 'sample' ? 'biosample' : 'Biosample';
                });
            case SearchResourceType.Sample:
                return input.replace(/biosample/gi, (match) => {
                    return match === 'biosample' ? 'sample' : 'Sample';
                }); 
            default:
                return input;
        }
    }

    getDisplayFieldsRequestEntries(): any[][] {
        const requestEntries = [];

        if(this.displayFieldsTabularFieldSetControl) {
            this.displayFieldsTabularFieldSetControl.controls.forEach((row: FormArray, rowIndex: number) => {
                const resource = this.resources[rowIndex];
                row.controls.forEach((field: BasespaceFormControl<any>) => {
                    requestEntries.push(
                        [
                            `${this.id}.${this.resourceType}.${this.transformResourceIdToFormDataId(String(resource.id))}.${field.fieldId}`,
                            field.value
                        ]
                    );
                });
            });
        }

        return requestEntries;
    }

    getRequestEntries(): any[][] {
        const displayFieldRequestEntries = this.getDisplayFieldsRequestEntries();

        if(this.value){
            const resourceRequestEntries = this.value.map((value) => {
                return [this.id, this.transformResourceIdToFormDataId(value)];
            });
            return [...resourceRequestEntries, ...displayFieldRequestEntries];
        } else {
            return [];
        }
    }
    
}